import { useEffect, useState } from 'react'
import { create } from 'zustand'

export type HubStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

interface ConnectionState {
  online: boolean
  hub: HubStatus
  setOnline: (online: boolean) => void
  setHub: (hub: HubStatus) => void
}

export const useConnection = create<ConnectionState>((set) => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  hub: 'idle',
  setOnline: (online) => set({ online }),
  setHub: (hub) => set({ hub }),
}))

export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/** A reconnect quicker than this shows nothing: the spinner would only flash. */
export const SHOW_WAITING_AFTER_MS = 500

export function isHubDown(hub: HubStatus): boolean {
  return hub === 'connecting' || hub === 'reconnecting' || hub === 'disconnected'
}

/** True once the phone is online but the hub has not been connected for SHOW_WAITING_AFTER_MS. */
export function useHubWaiting(): boolean {
  const online = useConnection((s) => s.online)
  const hub = useConnection((s) => s.hub)
  const down = online && isHubDown(hub)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!down) {
      setShown(false)
      return
    }
    const timer = setTimeout(() => setShown(true), SHOW_WAITING_AFTER_MS)
    return () => clearTimeout(timer)
  }, [down])
  return down && shown
}
